import { addDays, fromLocalDateTime, toLocalDate } from "./time";
import type { EpochSeconds, LocalDate } from "./time";

/*
 * Daily target from the published roster (T-180, ADR-0153, O-39): the same pure calculation as `RosterTarget` in the
 * backend, checked against `shared-test-cases/roster-targets.json`, so „Mein Dienstplan“ shows offline what the balance
 * counts. A roster governs the days of its period on which the person is employed at its establishment and the version
 * in force plans the person; a governed day takes the planned working time of the person's shifts beginning on it (zero
 * without a shift). The version in force on a day is the one in force when the day began; without one the first
 * published version counts, unless the day's month was locked before that version was published.
 */

/** Epoch milliseconds: the precision of publications and month locks, which may fall into the same second. */
export type EpochMilliseconds = number;

/** A published shift of the person. */
export interface RosterTargetShift {
  readonly start: EpochSeconds;
  readonly end: EpochSeconds;
  readonly plannedBreakSeconds: number;
}

/** A published version of a roster with the person's shifts in it. */
export interface RosterTargetVersion {
  /** The instant the version came into force. */
  readonly publishedAt: EpochMilliseconds;
  /** The instant the database recorded it, which orders it against a month lock; the publication instant when missing. */
  readonly recordedAt?: EpochMilliseconds;
  readonly shifts: readonly RosterTargetShift[];
}

/** A range of days. */
export interface RosterTargetRange {
  readonly from: LocalDate;
  readonly to: LocalDate;
}

/** The published versions of one roster for the person. */
export interface RosterTargetPlan {
  readonly from: LocalDate;
  readonly to: LocalDate;
  /** Time zone of the roster's establishment; a shift belongs to the local day it begins on. */
  readonly timeZone: string;
  readonly versions: readonly RosterTargetVersion[];
  /** Days of the person's employment at the roster's establishment; every day of the period when missing. */
  readonly employed?: readonly RosterTargetRange[];
}

/**
 * Returns the planned working time of a shift: its duration minus the planned break, never negative.
 *
 * @param shift - The shift.
 * @returns The planned working time in seconds.
 */
export function plannedWorkSeconds(shift: RosterTargetShift): number {
  const work = Math.max(0, shift.end - shift.start - shift.plannedBreakSeconds);

  return work;
}

/**
 * Returns the targets of the days the rosters govern.
 *
 * @param plans - The rosters of the person.
 * @param locks - The instant each month locked for the person was locked, keyed by its first day.
 * @returns The target of every governed day in seconds; days no roster governs are missing.
 */
export function rosterDayTargets(
  plans: readonly RosterTargetPlan[],
  locks: ReadonlyMap<LocalDate, EpochMilliseconds>,
): Map<LocalDate, number> {
  const targets = new Map<LocalDate, number>();

  for (const plan of plans) {
    addPlan(targets, plan, locks);
  }

  return targets;
}

/**
 * Adds the governed days of one roster.
 *
 * @param targets - The targets so far.
 * @param plan - The roster.
 * @param locks - The month locks.
 */
function addPlan(
  targets: Map<LocalDate, number>,
  plan: RosterTargetPlan,
  locks: ReadonlyMap<LocalDate, EpochMilliseconds>,
): void {
  const versions = [...plan.versions].sort((left, right) => left.publishedAt - right.publishedAt);
  const perVersion = new Map<RosterTargetVersion, Map<LocalDate, number>>();

  for (let date = plan.from; date <= plan.to; date = addDays(date, 1)) {
    const employed =
      plan.employed === undefined ||
      plan.employed.some((range) => range.from <= date && date <= range.to);
    const inForce = employed ? versionInForce(versions, date, plan.timeZone, locks) : undefined;

    if (inForce === undefined || inForce.shifts.length === 0) {
      continue;
    }

    let perDay = perVersion.get(inForce);

    if (perDay === undefined) {
      perDay = plannedPerDay(inForce, plan.timeZone);
      perVersion.set(inForce, perDay);
    }

    targets.set(date, (targets.get(date) ?? 0) + (perDay.get(date) ?? 0));
  }
}

/**
 * Returns the version in force on a day: the one in force when the day began, otherwise the first published one unless
 * the day's month was locked before it.
 *
 * @param versions - The versions in order of publication.
 * @param date - The day.
 * @param timeZone - Time zone of the establishment.
 * @param locks - The month locks.
 * @returns The version, or `undefined` if none governs the day.
 */
function versionInForce(
  versions: readonly RosterTargetVersion[],
  date: LocalDate,
  timeZone: string,
  locks: ReadonlyMap<LocalDate, EpochMilliseconds>,
): RosterTargetVersion | undefined {
  const dayStart = fromLocalDateTime(date, "00:00", timeZone) * 1000;
  const atStart = versions.filter((version) => version.publishedAt <= dayStart).at(-1);
  const first = versions[0];
  const lockedAt = locks.get(`${date.slice(0, 7)}-01`);
  const lockedBefore =
    first !== undefined &&
    lockedAt !== undefined &&
    (first.recordedAt ?? first.publishedAt) > lockedAt;
  const inForce = atStart ?? (lockedBefore ? undefined : first);

  return inForce;
}

/**
 * Sums the planned working time of a version per local day of begin.
 *
 * @param version - The version.
 * @param timeZone - Time zone of the establishment.
 * @returns The planned seconds per day.
 */
function plannedPerDay(version: RosterTargetVersion, timeZone: string): Map<LocalDate, number> {
  const perDay = new Map<LocalDate, number>();

  for (const shift of version.shifts) {
    const day = toLocalDate(shift.start, timeZone);
    perDay.set(day, (perDay.get(day) ?? 0) + plannedWorkSeconds(shift));
  }

  return perDay;
}
